import { useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { Add, ArrowBack } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "components/Layout";
import ManagementPageShell from "components/management/ManagementPageShell";
import {
  MANAGEMENT_TABS_SX,
  MANAGEMENT_TABLE_AREA_SX,
} from "components/management/managementTheme";
import PaginationControls from "components/fileStorage/PaginationControls";
import {
  BackendUserAutocompleteListbox,
  backendUserAutocompleteComponentsProps,
  renderBackendUserAutocompleteOption,
} from "components/common/BackendUserLabelBlock";
import { useBackendUsersAutocompleteInfinite } from "features/adminUsers/useBackendUsersAutocompleteInfinite";
import { formatBackendUserOptionLabel } from "utils/backendUserLabel";
import type { BackendUser } from "types/interfaces/adminUsers";
import { useGetPdfJobsQuery } from "features/pdfTools/pdfTools";
import JobsTable from "components/pdfTools/JobsTable";
import JobDetails from "components/pdfTools/JobDetails";
import NewSplitJobDialog from "components/pdfTools/NewSplitJobDialog";

export default function SplittingToolsPage() {
  const navigate = useNavigate();
  const { jobId } = useParams<{ jobId?: string }>();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [userFilter, setUserFilter] = useState<BackendUser | null>(null);
  const [userSearch, setUserSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);

  const {
    options: userOptions,
    loading: usersLoading,
    onListboxScroll: usersListboxOnScroll,
  } = useBackendUsersAutocompleteInfinite({
    skip: !!jobId,
    page_size: 50,
    search: userSearch,
    value: userFilter,
  });

  const { data, isFetching } = useGetPdfJobsQuery(
    {
      page,
      page_size: pageSize,
      user_id: userFilter?.id ?? null,
    },
    { skip: !!jobId, refetchOnMountOrArgChange: true, pollingInterval: 15000 },
  );

  const rows = useMemo(() => data?.results ?? [], [data]);
  const totalItems = data?.count ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  return (
    <Layout title="Splitting Tools" content="Splitting Tools" noScroll>
      <ManagementPageShell
        title="Splitting Tools"
        headerExtra={
          jobId ? (
            <Button
              variant="outlined"
              startIcon={<ArrowBack />}
              onClick={() => navigate("/splitting-tools")}
            >
              Back to jobs
            </Button>
          ) : (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setDialogOpen(true)}
            >
              New Split Job
            </Button>
          )
        }
      >
        {jobId ? (
          <JobDetails jobId={jobId} />
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              flex: 1,
              gap: 1.5,
            }}
          >
            <Tabs value="pdf_splitter" sx={MANAGEMENT_TABS_SX}>
              <Tab value="pdf_splitter" label="PDF Splitter" />
            </Tabs>

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 260 }}>
                Split multi-employee payroll PDFs (W-2, 1095, paystubs) into one
                file per employee, review the outcome and save it to the
                client&apos;s Data Storage.
              </Typography>
              <Autocomplete
                sx={{ width: { xs: "100%", sm: 300 } }}
                options={userOptions}
                loading={usersLoading}
                componentsProps={backendUserAutocompleteComponentsProps}
                ListboxComponent={BackendUserAutocompleteListbox}
                ListboxProps={{ onScroll: usersListboxOnScroll }}
                value={userFilter}
                onChange={(_e, v) => {
                  setUserFilter(v);
                  setPage(1);
                }}
                getOptionLabel={formatBackendUserOptionLabel}
                renderOption={renderBackendUserAutocompleteOption}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                size="small"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Filter by client user"
                    onChange={(e) => setUserSearch(e.target.value)}
                  />
                )}
              />
            </Box>

            <Box sx={MANAGEMENT_TABLE_AREA_SX}>
              <JobsTable
                rows={rows}
                loading={isFetching}
                onRowClick={(id) => navigate(`/splitting-tools/${id}`)}
              />
            </Box>

            <PaginationControls
              currentLimit={pageSize}
              onLimitChange={(limit) => {
                setPageSize(limit);
                setPage(1);
              }}
              totalItems={totalItems}
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
              size="small"
            />
          </Box>
        )}

        <NewSplitJobDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
        />
      </ManagementPageShell>
    </Layout>
  );
}
